import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as LucideIcons from "lucide-react";
import { LucideIcon } from "lucide-react";
import { FontWrapper } from "../common/FontWrapper";
import { EditModeProvider } from "../../contexts/EditModeContext";
import { EditModePanel } from "../common/EditModePanel";
import { ScientificBasisInfo } from "../common/ScientificBasisInfo";
import { EditableText } from "../common/EditableText";
import { VishingPhone } from "../common/VishingPhone";
import { useIsMobile } from "../ui/use-mobile";
import { CallToAction } from "../ui/CallToAction";
import { deepMerge } from "../../utils/deepMerge";
import { CommentPinsOverlay } from "../ui/comment-pins-overlay";
import { DecorativeSceneIcon } from "../../presentation/DecorativeSceneIcon";
import { CARD_PHONE_STAGE_CLASS, sceneMainClassName, sceneSubtitleClassName, sceneTitleClassName, useCardSceneLayout } from "../../presentation/cardSceneLayout";
import type { VishingSceneConfig } from "../configs/educationConfigs";

interface VishingSceneProps {
  config: VishingSceneConfig;
  appConfig?: any;
  onNextSlide?: () => void;
  sceneId?: string | number;
  selectedLanguage?: string;
  onCallCompleted?: (completed: boolean) => void;
}

const DEFAULT_CONTAINER_CLASS = "flex flex-col items-center justify-center h-full text-center";

const getIconComponent = (iconName?: string): LucideIcon => {
  if (!iconName) return LucideIcons.Phone;
  const camelCaseName = iconName
    .split("-")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");

  if (camelCaseName in LucideIcons) {
    return LucideIcons[camelCaseName as keyof typeof LucideIcons] as LucideIcon;
  }

  return LucideIcons.Phone;
};

const resolveText = (value?: string | { mobile?: string; desktop?: string }, isMobile?: boolean) => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  return isMobile ? value.mobile : value.desktop;
};

export const VishingScene = memo(function VishingScene({
  config,
  appConfig,
  onNextSlide,
  sceneId,
  selectedLanguage,
  onCallCompleted
}: VishingSceneProps) {
  const [editChanges, setEditChanges] = useState<Partial<VishingSceneConfig>>({});
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [configKey, setConfigKey] = useState(0);
  const [callStatus, setCallStatus] = useState<"idle" | "requesting-mic" | "connecting" | "live" | "ending" | "error">("idle");
  const [hasCompletedCall, setHasCompletedCall] = useState(false);
  const [hasCallEnded, setHasCallEnded] = useState(false);
  const callLiveStartRef = useRef<number | null>(null);
  const MIN_CALL_DURATION_MS = 30000;
  const prevStatusRef = useRef<typeof callStatus>("idle");
  const isMobile = useIsMobile();
  const layout = useCardSceneLayout();
  const hideSceneChrome = layout.isCard && !isInEditMode;

  useEffect(() => {
    setConfigKey(prev => prev + 1);
    setEditChanges({});
    setHasCompletedCall(false);
    setHasCallEnded(false);
    setCallStatus("idle");
  }, [config.title, config.subtitle]);

  const currentConfig = useMemo(() => {
    return deepMerge(config, editChanges);
  }, [config, editChanges]);

  useEffect(() => {
    if (!isInEditMode) {
      setEditChanges({});
    }
  }, [isInEditMode]);

  useEffect(() => {
    if (callStatus === "live") {
      callLiveStartRef.current = performance.now();
      setHasCompletedCall(false);
      setHasCallEnded(false);
      return;
    }

    if (callStatus === "idle" && callLiveStartRef.current !== null) {
      const duration = performance.now() - callLiveStartRef.current;
      const meetsDuration = duration >= MIN_CALL_DURATION_MS;
      setHasCompletedCall(meetsDuration);
      setHasCallEnded(true);
      callLiveStartRef.current = null;
      return;
    }

    if (callStatus === "error") {
      callLiveStartRef.current = null;
    }
    prevStatusRef.current = callStatus;
  }, [callStatus]);

  const IconComponent = useMemo(() => {
    return getIconComponent(currentConfig.iconName);
  }, [currentConfig.iconName]);

  const handleSave = useCallback((newConfig: any) => {
    setEditChanges(newConfig);
  }, []);

  const microlearningId = (appConfig as any)?.microlearning_id || "unknown";
  const startLabel = resolveText(currentConfig.callToActionText, isMobile) || "Start Call Practice";
  const canAdvance = hasCompletedCall && hasCallEnded;

  useEffect(() => {
    onCallCompleted?.(canAdvance);
  }, [canAdvance, onCallCompleted]);

  return (
    <EditModeProvider
      key={configKey}
      initialConfig={currentConfig}
      sceneId={sceneId?.toString()}
      onSave={handleSave}
      onEditModeChange={setIsInEditMode}
    >
      <EditModePanel sceneId={sceneId} sceneLabel={(currentConfig as any)?.title} appConfig={appConfig} />
      <ScientificBasisInfo
        config={currentConfig}
        sceneType={(currentConfig as any)?.scene_type || "vishing_simulation"}
      />
      <FontWrapper className={layout.isCard ? "flex h-full min-h-0 flex-1 flex-col" : undefined}>
        <main
          className={sceneMainClassName(DEFAULT_CONTAINER_CLASS, layout, layout.isCard ? "relative h-full min-h-0 flex-1 overflow-hidden" : "relative")}
          role="main"
          aria-labelledby="vishing-scene-title"
          data-scene-type={(config as any)?.scene_type || "vishing_simulation"}
          data-scene-id={sceneId as any}
          data-testid="scene-vishing"
          data-comment-surface="true"
        >
          <DecorativeSceneIcon className={`mb-1 sm:mb-2 p-3 ${isInEditMode ? "glass-border-3-no-overflow" : "glass-border-3"} relative`}>
            <IconComponent size={40} className="text-[#1C1C1E] dark:text-[#F2F2F7]" aria-hidden="true" />
          </DecorativeSceneIcon>

          {hideSceneChrome ? (
            <h1 id="vishing-scene-title" className="sr-only">{currentConfig.title}</h1>
          ) : (
            <>
              <h1 id="vishing-scene-title" className={sceneTitleClassName(layout)}>
                <EditableText configPath="title" placeholder="Enter title">
                  {currentConfig.title}
                </EditableText>
              </h1>
              <p className={sceneSubtitleClassName(layout)}>
                <EditableText configPath="subtitle" placeholder="Enter subtitle">
                  {currentConfig.subtitle}
                </EditableText>
              </p>
            </>
          )}

          <div className={layout.isCard ? CARD_PHONE_STAGE_CLASS : "mt-4 flex w-full justify-center"}>
            <VishingPhone
              compact={layout.isCard}
              microlearningId={microlearningId}
              language={(selectedLanguage || "en-gb").toLowerCase()}
              startLabel={startLabel}
              prompt={currentConfig.prompt}
              firstMessage={currentConfig.firstMessage}
              callerName={currentConfig.callerName}
              callerNumber={currentConfig.callerNumber}
              statusTexts={currentConfig.statusTexts}
              onStatusChange={setCallStatus}
            />
          </div>

          {currentConfig.texts?.privacyNotice && (
            <div className={layout.isCard ? "mt-2 max-w-sm shrink-0 rounded-xl bg-black/5 px-3 py-1.5 text-center text-[11px] leading-snug text-[#1C1C1E]/70 dark:bg-white/5 dark:text-[#F2F2F7]/70" : "mt-3 max-w-xl rounded-xl bg-black/5 px-4 py-2 text-xs text-[#1C1C1E]/70 dark:bg-white/5 dark:text-[#F2F2F7]/70"}>
              <EditableText configPath="texts.privacyNotice" placeholder="Privacy notice text">
                {currentConfig.texts.privacyNotice}
              </EditableText>
            </div>
          )}

          {canAdvance && (
            <CallToAction
              text={typeof currentConfig.successCallToActionText === "string" ? currentConfig.successCallToActionText : undefined}
              mobileText={typeof currentConfig.successCallToActionText === "object" ? currentConfig.successCallToActionText.mobile : undefined}
              desktopText={typeof currentConfig.successCallToActionText === "object" ? currentConfig.successCallToActionText.desktop : undefined}
              onClick={onNextSlide}
              disabled={!onNextSlide}
              isRTL={(currentConfig as any)?.isRTL}
            />
          )}
        </main>
      </FontWrapper>
      <CommentPinsOverlay />
    </EditModeProvider>
  );
});
